/** @jsxImportSource @emotion/react */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, TextField, Typography, Paper } from "@mui/material";
import { styled } from "@mui/system";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { toast } from "react-toastify";

// Styled components
const Container = styled(Box)({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  height: "100vh",
  background: "linear-gradient(to right, #6a11cb, #2575fc)",
});

const OTPCard = styled(Paper)({
  padding: "40px 30px",
  maxWidth: 400,
  width: "100%",
  textAlign: "center",
  borderRadius: 16,
  boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
});

const ErrorText = styled(Typography)({
  color: "red",
  marginTop: 10,
});

// Validation schema
const OTPSchema = Yup.object().shape({
  otp: Yup.string()
    .matches(/^[0-9]{4,6}$/, "Enter a valid OTP")
    .required("OTP is required"),
});

const OTPPage = () => {
  const navigate = useNavigate();
  const phone = localStorage.getItem("phoneNumber");
  const [error, setError] = useState("");
  const [resending, setResending] = useState(false);

  // Verify OTP
  const handleVerify = async (values, { setSubmitting }) => {
    setError("");
    try {
      const res = await axios.post(
        "https://api.cansunsari.org.np/dspro/api/auth/otp-verification",
        {
          phoneNumber: phone,
          otp: values.otp,
        },
        { headers: { "Content-Type": "application/json" } },
      );
      const token = res.data.accessToken;

      if (token) {
        const decoded = jwtDecode(token);
        const role = decoded.role;
        localStorage.setItem("authToken", token);
        localStorage.setItem("role", role);
        console.log(role);
      }

      navigate("/dashboard");
      window.location.reload();
      toast.success("OTP Verified ✅");
    } catch (err) {
      console.error(err);
      setError("Invalid OTP, please try again");
    } finally {
      setSubmitting(false);
    }
  };

  // Resend OTP
  const handleResend = async () => {
    setError("");
    setResending(true);
    try {
      await axios.post(
        "https://api.cansunsari.org.np/dspro/api/auth/login",
        { phoneNumber: phone },
        { headers: { "Content-Type": "application/json" } },
      );
      toast.success("OTP resent successfully ✅");
    } catch (err) {
      console.error(err);
      setError("Failed to resend OTP. Please try again.");
    } finally {
      setResending(false);
    }
  };

  return (
    <Container>
      <OTPCard>
        <Typography variant="h4" gutterBottom>
          Enter OTP
        </Typography>
        <Typography variant="body2" gutterBottom>
          OTP sent to {phone}
        </Typography>

        <Formik
          initialValues={{ otp: "" }}
          validationSchema={OTPSchema}
          onSubmit={handleVerify}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            isSubmitting,
          }) => (
            <Form>
              <TextField
                fullWidth
                id="otp"
                name="otp"
                label="OTP"
                placeholder="Enter OTP"
                value={values.otp}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.otp && Boolean(errors.otp)}
                helperText={touched.otp && errors.otp}
                margin="normal"
              />

              {error && <ErrorText>{error}</ErrorText>}

              {/* Resend OTP link below the field */}
              <Box display="flex" justifyContent="flex-end" mt={1}>
                <Button
                  onClick={handleResend}
                  disabled={resending}
                  sx={{
                    textTransform: "none", // keep as plain text
                    fontSize: "0.85rem",
                    p: 0,
                    minWidth: "auto",
                    color: "#fff", // white text (or use "#2196f3" for blue)
                    "&:hover": {
                      backgroundColor: "transparent", // no hover background
                      textDecoration: "underline", // underline on hover
                    },
                  }}
                >
                  {resending ? "Resending..." : "Resend OTP"}
                </Button>
              </Box>

              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                sx={{ mt: 2, py: 1.5 }}
                disabled={isSubmitting}
              >
                Verify OTP
              </Button>
            </Form>
          )}
        </Formik>
      </OTPCard>
    </Container>
  );
};

export default OTPPage;
